/**
 * Block 11 (2026-05-27): ФОП ЗСО quarterly calculations page.
 *
 * List + Editor modal (preview + calculate-and-post).
 * Filter org_id обмежений по is_fop_zso (tax_system='FOP_GENERAL').
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { orgs } from '@/api/catalogs';
import { useGridColumnWidths } from '@/hooks/useGridColumnWidths';
import {
  fopZso,
  type FopZsoQuarterlyCalculation,
  type FopZsoQuarterlyPreview,
} from '@/api/fop_zso';
import { FOP_EXPENSE_CATEGORY_LABELS, type FopExpenseCategory } from '@/shared/fop_expense_categories';


const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


const QUARTER_LABELS: Record<number, string> = {
  1: 'Q1 (січень-березень)',
  2: 'Q2 (квітень-червень)',
  3: 'Q3 (липень-вересень)',
  4: 'Q4 (жовтень-грудень)',
};


export default function FopZsoQuarterlyCalculationsPage() {
  const [orgId, setOrgId] = useState<string>('');
  const [editorOpen, setEditorOpen] = useState(false);
  // Персист ширин колонок ag-Grid (дзеркало DataTable-персисту).
  const colW = useGridColumnWidths('fop-zso-quarterly');

  const orgListQ = useQuery({
    queryKey: ['orgs-fop-zso'],
    queryFn: () => orgs.list({ limit: 200 }),
  });
  const fopZsoOrgs = useMemo(
    () => (orgListQ.data?.items ?? []).filter((o) => o.tax_system === 'FOP_GENERAL'),
    [orgListQ.data],
  );

  const list = useQuery({
    queryKey: ['fop-zso-quarterly', orgId],
    queryFn: () => fopZso.list({
      organization_id: orgId || undefined,
      limit: 200,
    }),
    enabled: fopZsoOrgs.length > 0,
  });

  const cols = useMemo<ColDef<FopZsoQuarterlyCalculation>[]>(() => [
    { field: 'document_number', headerName: '№', width: 120 },
    {
      field: 'document_date', headerName: 'Дата', width: 110,
      valueFormatter: (p) =>
        p.value ? new Date(p.value as string).toLocaleDateString('uk-UA') : '',
    },
    { field: 'period_year', headerName: 'Рік', width: 80, cellStyle: { textAlign: 'right' } },
    {
      field: 'period_quarter', headerName: 'Кв', width: 70,
      valueFormatter: (p) => `Q${p.value}`,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'revenue_for_period', headerName: 'Дохід', width: 130,
      valueFormatter: (p) => fmt(p.value as string),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'allowed_expenses_amount', headerName: 'Витрати', width: 130,
      valueFormatter: (p) => fmt(p.value as string),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'net_income', headerName: 'Чистий дохід', width: 140,
      valueFormatter: (p) => fmt(p.value as string),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'pdfo_amount', headerName: 'ПДФО', width: 110,
      valueFormatter: (p) => fmt(p.value as string),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'wz_amount', headerName: 'ВЗ', width: 100,
      valueFormatter: (p) => fmt(p.value as string),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'esv_amount', headerName: 'ЄСВ', width: 110,
      valueFormatter: (p) => fmt(p.value as string),
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      valueFormatter: (p) => (p.value ? '✓ так' : 'ні'),
    },
  ], []);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>ФОП ЗСО — Квартальні розрахунки</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— усі ФОП ЗСО юрособи —</option>
            {fopZsoOrgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <button
            className="primary"
            onClick={() => setEditorOpen(true)}
            disabled={fopZsoOrgs.length === 0}
          >
            + Розрахувати квартал
          </button>
        </div>
      </div>
      {fopZsoOrgs.length === 0 && !orgListQ.isLoading && (
        <div style={{ padding: '1rem', color: '#92400e', background: '#fef3c7', borderRadius: 6 }}>
          У вашому тенанті немає організацій-ФОП ЗСО (tax_system=FOP_GENERAL).
        </div>
      )}
      <div className="ag-theme-quartz grid" style={{ height: 'calc(100vh - 200px)' }}>
        <AgGridReact<FopZsoQuarterlyCalculation>
          rowData={list.data?.items ?? []}
          columnDefs={cols}
          loading={list.isLoading}
          onColumnResized={colW.onColumnResized}
          onGridReady={(e) => { colW.applyTo(e.api); }}
        />
      </div>
      {editorOpen && (
        <FopZsoEditor
          orgs={fopZsoOrgs}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </>
  );
}


interface OrgOption { id: string; name: string }

function FopZsoEditor({ orgs, onClose }: { orgs: OrgOption[]; onClose: () => void }) {
  const qc = useQueryClient();
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
  const [orgId, setOrgId] = useState(orgs[0]?.id ?? '');
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(currentQuarter);
  const [preview, setPreview] = useState<FopZsoQuarterlyPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: () => fopZso.preview({
      organization_id: orgId,
      period_year: year,
      period_quarter: quarter,
    }),
    onSuccess: (data) => { setPreview(data); setError(null); },
    onError: (e) => setError(extractError(e)),
  });

  const postMut = useMutation({
    mutationFn: () => fopZso.calculateAndPost({
      organization_id: orgId,
      period_year: year,
      period_quarter: quarter,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fop-zso-quarterly'] });
      onClose();
    },
    onError: (e) => setError(extractError(e)),
  });

  return (
    <div
      data-testid="fop-zso-editor-modal"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div className="card" style={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <h3 style={{ margin: 0 }}>Розрахунок ФОП ЗСО квартальний</h3>
        <div className="field">
          <label>Юрособа</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Рік</label>
            <input type="number" value={year} min={2020} max={2100}
                   onChange={(e) => setYear(Number(e.target.value))} />
          </div>
          <div className="field" style={{ flex: 2 }}>
            <label>Квартал</label>
            <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
              {[1,2,3,4].map((q) => (
                <option key={q} value={q}>{QUARTER_LABELS[q]}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={() => previewMut.mutate()}
          disabled={!orgId || previewMut.isPending}
          style={{ marginTop: '0.5rem' }}
        >
          Розрахувати preview
        </button>
        {error && <div className="error" style={{ marginTop: '0.5rem' }}>{error}</div>}
        {preview && (
          <div data-testid="fop-zso-preview" style={{ marginTop: '1rem', padding: '0.75rem', background: '#f0f9ff', borderRadius: 6 }}>
            <h4 style={{ marginTop: 0 }}>Розрахунок</h4>
            <p>Дохід за квартал: <strong>{fmt(preview.revenue_for_period)} грн</strong></p>
            <p>Визнані витрати: <strong>{fmt(preview.allowed_expenses_amount)} грн</strong></p>
            <p>Чистий дохід: <strong>{fmt(preview.net_income)} грн</strong></p>
            <h4>Податки</h4>
            <p>ПДФО ({preview.pdfo_rate}%): <strong>{fmt(preview.pdfo_amount)} грн</strong></p>
            <p>ВЗ ({preview.wz_rate}%): <strong>{fmt(preview.wz_amount)} грн</strong></p>
            <p>
              ЄСВ ({preview.esv_rate}%): <strong>{fmt(preview.esv_amount)} грн</strong>
              <small style={{ color: '#475569', marginLeft: 8 }}>
                (мін. {fmt(preview.min_insurance_per_quarter)} грн/квартал)
              </small>
            </p>
            <p style={{ fontSize: '1.1rem' }}>
              <strong>Разом:</strong>{' '}
              {fmt(
                Number(preview.pdfo_amount)
                + Number(preview.wz_amount)
                + Number(preview.esv_amount),
              )} грн
            </p>
            {Object.keys(preview.expenses_breakdown).length > 0 && (
              <>
                <h4>Витрати за категоріями</h4>
                <table style={{ width: '100%', fontSize: '0.85rem' }}>
                  <tbody>
                    {Object.entries(preview.expenses_breakdown).map(([cat, amount]) => (
                      <tr key={cat}>
                        <td>{FOP_EXPENSE_CATEGORY_LABELS[cat as FopExpenseCategory] ?? cat}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(amount)} грн</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button onClick={onClose}>Скасувати</button>
          <button
            className="primary"
            disabled={!preview || postMut.isPending}
            onClick={() => postMut.mutate()}
          >
            Розрахувати та провести
          </button>
        </div>
      </div>
    </div>
  );
}


function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Помилка';
  }
  return String(e);
}
